// Local-first storage. Like WhatsApp, the device keeps its own copy of every
// chat; the server archive (MAM) is only used to fill it in.
import { openDB, type IDBPDatabase } from "idb";
import type { Profile } from "./api";
import { plainText } from "./util";

export const Status = {
  Failed: -1,
  Pending: 0,
  Sent: 1,
  Delivered: 2,
  Read: 3,
} as const;
export type Status = (typeof Status)[keyof typeof Status];

export type Kind = "text" | "image" | "video" | "audio" | "voice" | "file" | "system" | "contact" | "location" | "call" | "poll" | "event" | "sticker";

/** A call in the chat history. 1:1: posted by the caller when the call ends.
 * Group: the invite, `result` "ended" once everyone left. */
export interface CallEntry {
  sid: string;
  media: "audio" | "video";
  result?: "answered" | "missed" | "declined" | "busy" | "failed" | "ended";
  duration?: number; // seconds
  group?: boolean;
}

export interface ContactCard {
  name: string;
  phone: string; // E.164
  jid?: string; // set when the contact is on xmm
}

/** A sticker's pack (free packs from the sticker store) or nothing (your own). */
export interface StickerRef {
  pack?: string; // 32 hex
  key?: string; // 64 hex
  id?: number;
  emoji?: string;
}

/** A poll (like WhatsApp's): votes arrive as encrypted "vote" ops. */
export interface Poll {
  question: string;
  options: { id: string; text: string }[];
  multi: boolean; // more than one answer allowed
  votes?: Record<string, { choices: string[]; ts: number }>; // voter bare JID ->
}

export type RSVP = "going" | "maybe" | "no";

/** An event: answers arrive as encrypted "rsvp" ops; the organizer can cancel it. */
export interface ChatEvent {
  name: string;
  description?: string;
  start: number; // ms
  end?: number; // ms
  place?: string;
  call?: "voice" | "video"; // a call link: join the chat's call
  canceled?: boolean;
  rsvps?: Record<string, { answer: RSVP; ts: number }>; // bare JID ->
}

export interface Location {
  lat: number;
  lon: number;
  accuracy?: number; // metres
  name?: string;
  /** live location: shared until `until` (ms) unless stopped earlier */
  live?: { until: number; stopped?: boolean; updatedAt: number };
}

/** A group event posted by the server ("Alice added Bob"). */
export interface SystemEvent {
  type: string; // created | added | removed | left | joined | promoted | demoted | name | description | photo | settings
  actor: string;
  targets: string[];
  value: string;
}

export interface Mention {
  jid: string;
  begin: number;
  end: number;
}

export interface Media {
  url?: string; // set once uploaded
  mime: string;
  size: number;
  name?: string;
  width?: number;
  height?: number;
  duration?: number; // seconds
  pages?: number; // documents (PDF): page count
  gif?: boolean; // a GIF (sent as a looping silent MP4)
  note?: boolean; // a video note (round, like a voice note)
  thumb?: string; // tiny JPEG data URL, shown blurred while loading (PDFs: the first page)
  waveform?: number[]; // voice notes: 0..100 per bar
  blob?: Blob; // outgoing only, until the upload finishes
  /** end-to-end encrypted media: AES-GCM key/iv and ciphertext hash (base64) */
  key?: string;
  iv?: string;
  sha256?: string;
}

export interface LinkPreview {
  url: string;
  title: string;
  description?: string;
  site_name?: string;
  thumb?: string;
  large?: boolean; // a wide image: shown as a big card
}

export interface Quote {
  id: string;
  from: string;
  /** snapshot of the quoted message, taken when this message was stored */
  kind?: Kind;
  text?: string;
  /** "Reply privately": the group the quoted message is from */
  chat?: string;
}

export interface Msg {
  key: string; // chat + "\0" + id
  id: string; // sender-generated id (also origin-id)
  chat: string; // bare JID of the other party
  from: string; // bare JID of the author
  dir: "in" | "out";
  kind: Kind;
  body: string; // text, or the caption of media
  ts: number; // ms
  status: Status; // meaningful for dir === "out"
  stanzaId?: string; // server archive id
  media?: Media;
  replyTo?: Quote;
  preview?: LinkPreview;
  forwarded?: boolean;
  reactions?: Record<string, string>; // bare JID -> emoji
  editedAt?: number;
  deleted?: boolean; // deleted for everyone
  hidden?: boolean; // deleted for me (tombstone keeps dedupe working)
  system?: SystemEvent;
  /** encrypted, but not for this device (e.g. sent before it was linked) */
  undecryptable?: boolean;
  /** arrived without end-to-end encryption (other XMPP clients, server) */
  insecure?: boolean;
  /** disappearing messages: timer (s) it was sent with, and when it expires (ms) */
  ttl?: number;
  expiresAt?: number;
  /** view-once photo/video; `viewed` once opened (media is then wiped) */
  viewOnce?: boolean;
  viewed?: boolean;
  contact?: ContactCard;
  location?: Location;
  poll?: Poll;
  event?: ChatEvent;
  /** stickers from a public pack: where it's from, so others can add the pack */
  sticker?: StickerRef;
  /** pinned to the top of the chat until `until` (ms), by `by` */
  pin?: { until: number; by: string; ts: number };
  /** a voice message's transcript, made on this device (never sent) */
  transcript?: string;
  /** a translation made on this device */
  translation?: { text: string; from: string; to: string };
  /** "mentioned you in their status": which status (its owner is the sender) */
  statusRef?: { id: string; kind: "text" | "image" | "video"; text?: string };
  /** a disappearing message someone kept: it no longer disappears */
  kept?: { by: string; ts: number };
  call?: CallEntry;
  /** channels: reaction counts (not who) and our own reaction */
  chanReactions?: Record<string, number>;
  myReaction?: string;
  /** our copy of a broadcast-list message: the list's chat JID */
  bcast?: string;
  mentions?: Mention[];
  /** groups: per-member delivery (d) / read (r) times for our own messages */
  receipts?: Record<string, { d?: number; r?: number }>;
}

export interface Chat {
  jid: string;
  lastBody: string; // preview line for the chat list
  lastTs: number;
  lastDir: "in" | "out" | "";
  lastStatus: Status;
  unread: number;
  lastId?: string;
  lastFrom?: string; // author of the last message (groups show "Alice: …")
  mention?: boolean; // an unread message mentions us
  lastReadId?: string; // newest incoming message we sent a read marker for
  draft?: string;
}

export interface ContactProfile extends Profile {
  fetchedAt: number;
}

type Schema = {
  messages: { key: string; value: Msg; indexes: { byChatTs: [string, number]; byStatus: number; byExpiry: number } };
  chats: { key: string; value: Chat };
  receipts: { key: string; value: { key: string; status: Status } };
  profiles: { key: string; value: ContactProfile };
  kv: { key: string; value: unknown };
};

export type DB = IDBPDatabase<Schema>;

export const msgKey = (chat: string, id: string) => chat + "\u0000" + id;

export function openStore(account: string): Promise<DB> {
  return openDB<Schema>("xmm:" + account, 3, {
    async upgrade(db, oldVersion, _v, tx) {
      if (oldVersion < 1) {
        const m = db.createObjectStore("messages", { keyPath: "key" });
        m.createIndex("byChatTs", ["chat", "ts"]);
        m.createIndex("byStatus", "status");
        db.createObjectStore("chats", { keyPath: "jid" });
        db.createObjectStore("receipts", { keyPath: "key" });
        db.createObjectStore("profiles", { keyPath: "jid" });
        db.createObjectStore("kv");
      }
      if (oldVersion < 3) {
        // v3: disappearing messages are swept by expiry time.
        tx.objectStore("messages").createIndex("byExpiry", "expiresAt");
      }
      if (oldVersion === 1) {
        // v2 added message kinds; older rows are all text.
        let cur = await tx.objectStore("messages").openCursor();
        while (cur) {
          if (!cur.value.kind) await cur.update({ ...cur.value, kind: "text" });
          cur = await cur.continue();
        }
      }
    },
  });
}

/** Newest `limit` visible messages of a chat older than `beforeTs`, oldest first. */
export async function messagesPage(db: DB, chat: string, limit: number, beforeTs = Infinity): Promise<Msg[]> {
  const range = IDBKeyRange.bound([chat, -Infinity], [chat, beforeTs], false, true);
  const out: Msg[] = [];
  let cur = await db.transaction("messages").store.index("byChatTs").openCursor(range, "prev");
  while (cur && out.length < limit) {
    if (!cur.value.hidden) out.push(cur.value);
    cur = await cur.continue();
  }
  return out.reverse();
}

/** Visible messages of a chat from `fromTs` (inclusive) to now, capped. */
export async function messagesSince(db: DB, chat: string, fromTs: number, limit: number): Promise<Msg[]> {
  const range = IDBKeyRange.bound([chat, fromTs], [chat, Infinity]);
  const out: Msg[] = [];
  let cur = await db.transaction("messages").store.index("byChatTs").openCursor(range);
  while (cur && out.length < limit) {
    if (!cur.value.hidden) out.push(cur.value);
    cur = await cur.continue();
  }
  return out;
}

export function callLabel(c: CallEntry | undefined, dir: "in" | "out"): string {
  const what = c?.media === "video" ? "video call" : "voice call";
  if (c?.group) return c.result === "ended" ? `Group ${what} ended` : `Group ${what}`;
  if (c?.result === "answered") return (dir === "out" ? "Outgoing " : "Incoming ") + what;
  if (dir === "in") return "Missed " + what;
  return { declined: "Declined", busy: "Busy", failed: "Couldn't connect", missed: "No answer" }[c?.result as string] ?? "Outgoing " + what;
}

/** One-line summary used in the chat list, notifications and quotes. */
export function summarize(m: Pick<Msg, "kind" | "body" | "media" | "deleted" | "dir"> & Partial<Pick<Msg, "contact" | "location" | "undecryptable" | "call" | "poll" | "event" | "statusRef">>): string {
  if (m.undecryptable) return "🔒 Message";
  if (m.deleted) return m.dir === "out" ? "You deleted this message" : "This message was deleted";
  if (m.statusRef) return m.dir === "out" ? "⭕ You mentioned them in your status" : "⭕ Mentioned you in their status";
  const cap = plainText(m.body.trim()); // without *formatting* markers
  switch (m.kind) {
    case "image":
      return "📷 " + (cap || "Photo");
    case "video":
      return m.media?.gif ? "🎞️ GIF" : m.media?.note ? "📹 Video note" : "🎥 " + (cap || "Video");
    case "voice":
      return "🎤 Voice message";
    case "audio":
      return "🎵 " + (cap || m.media?.name || "Audio");
    case "file":
      return "📄 " + (cap || m.media?.name || "Document");
    case "contact":
      return "👤 " + (m.contact?.name || "Contact");
    case "location":
      return "📍 " + (m.location?.live ? "Live location" : m.location?.name || "Location");
    case "call":
      return (m.call?.media === "video" ? "📹 " : "📞 ") + callLabel(m.call, m.dir);
    case "sticker":
      return "🏷️ Sticker";
    case "poll":
      return "📊 " + (m.poll?.question || "Poll");
    case "event":
      return "📅 " + (m.event?.canceled ? "Canceled: " : "") + (m.event?.name || "Event");
  }
  return cap;
}
